import i18next from 'i18next';

/**
 * Generic formatting utilities
 */

/**
 * Format a file size in bytes to a human-readable string
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB", "350 KB")
 *
 * @example
 * ```ts
 * formatFileSize(1500000) // "1.4 MB"
 * formatFileSize(500) // "500 B"
 * ```
 */
export function formatFileSize(bytes?: number): string {
  if (!bytes || bytes === 0) return '-';

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(1)} KB`;
  }

  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

/**
 * Format a date to a localized date string
 * @param date - Date string or Date object
 * @param locale - Locale string (defaults to the UI language)
 * @returns Formatted date string
 *
 * @example
 * ```ts
 * formatDate('2024-01-15') // "15.1.2024" (UI language de) / "1/15/2024" (en)
 * formatDate('2024-01-15', 'en-US') // "1/15/2024"
 * ```
 */
export function formatDate(date?: string | Date, locale = i18next.language): string {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString(locale);
  } catch {
    return '-';
  }
}

/**
 * Format a date and time to a localized string
 * @param date - Date string or Date object
 * @param locale - Locale string (defaults to the UI language)
 * @returns Formatted date and time string
 *
 * @example
 * ```ts
 * formatDateTime('2024-01-15T10:30:00') // "15.1.2024, 10:30:00" (UI language de) / "1/15/2024, 10:30:00 AM" (en)
 * ```
 */
export function formatDateTime(date?: string | Date, locale = i18next.language): string {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString(locale);
  } catch {
    return '-';
  }
}

/**
 * Format a number with locale-specific formatting
 * @param value - Number to format
 * @param locale - Locale string (defaults to 'en-US')
 * @returns Formatted number string
 *
 * @example
 * ```ts
 * formatNumber(1234567) // "1,234,567"
 * ```
 */
export function formatNumber(value?: number, locale: string = 'en-US'): string {
  if (value === undefined || value === null) return '-';
  return value.toLocaleString(locale);
}

/**
 * Format a currency value
 * @param value - Amount to format
 * @param currency - Currency code (default: 'USD')
 * @param locale - Locale string (defaults to 'en-US')
 * @returns Formatted currency string
 *
 * @example
 * ```ts
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency(1234.56, 'EUR', 'de-DE') // "1.234,56 €"
 * ```
 */
export function formatCurrency(
  value?: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  if (value === undefined || value === null) return '-';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(value);
}

function microEurFractionDigits(absEur: number): number {
  if (absEur === 0 || absEur >= 1) return 2;
  return absEur >= 0.01 ? 4 : 6;
}

/**
 * Format an integer micro-EUR amount (1 EUR = 1_000_000) as a EUR currency
 * string. Precision adapts to magnitude so sub-cent amounts (e.g. a single
 * embedding call at €0.000018) stay visible instead of rounding to €0.00.
 */
export function formatMicroEur(microEur: number, locale?: string): string {
  const eur = microEur / 1_000_000;
  const maximumFractionDigits = microEurFractionDigits(Math.abs(eur));

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits
  }).format(eur);
}

/**
 * Truncate text to a maximum length with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length (default: 50)
 * @returns Truncated string
 *
 * @example
 * ```ts
 * truncateText('This is a very long text that needs truncating', 20)
 * // "This is a very lo..."
 * ```
 */
export function truncateText(text?: string, maxLength: number = 50): string {
  if (!text) return '-';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
}

/**
 * Render a fallback value when data is missing
 * @param value - The value to check
 * @param fallback - Fallback string (default: '-')
 * @returns The value or fallback
 *
 * @example
 * ```ts
 * renderFallback(null) // "-"
 * renderFallback('', 'N/A') // "N/A"
 * renderFallback('value') // "value"
 * ```
 */
export function renderFallback(value?: string | number | null, fallback: string = '-'): string {
  if (value === undefined || value === null || value === '') return fallback;
  return String(value);
}
